import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Package,
  ShoppingBag,
  Truck,
  Users,
} from "lucide-react";

export default function DashboardOverviewComponent({
  totalProducts = 0,
  totalOrders = 0,
  pendingShipping = 0,
  registeredUsers = 0,
}) {
  const stats = [
    {
      title: "Total Products",
      value: totalProducts,
      description: "Active catalog items in the store.",
      icon: Package,
      accent: "from-sky-500 to-cyan-500",
      link: "/products",
    },
    {
      title: "Total Orders",
      value: totalOrders,
      description: "Placed orders waiting in the pipeline.",
      icon: ShoppingBag,
      accent: "from-violet-500 to-fuchsia-500",
      link: "/orders",
    },
    {
      title: "Pending Shipping",
      value: pendingShipping,
      description: "Orders ready for dispatch and tracking.",
      icon: Truck,
      accent: "from-amber-500 to-orange-500",
      link: "/shipping",
    },
    {
      title: "Registered Users",
      value: registeredUsers,
      description: "Customer and admin accounts on record.",
      icon: Users,
      accent: "from-emerald-500 to-teal-500",
      link: "/users",
    },
  ];

  const formatValue = (value) =>
    new Intl.NumberFormat("en-US").format(Number(value) || 0);

  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white/90 p-5 shadow-[0_18px_50px_rgba(15,23,42,0.08)] backdrop-blur-sm">

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((item) => (
          <Link
            key={item.title}
            to={item.link}
            aria-label={`${item.title}: ${formatValue(item.value)}`}
            className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-5 text-slate-900 transition-all duration-300 hover:-translate-y-1 hover:border-transparent hover:bg-slate-900 hover:text-white hover:shadow-[0_24px_60px_rgba(15,23,42,0.18)]"
          >
            <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${item.accent}`} />
            <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-slate-900/5 transition-transform duration-300 group-hover:scale-125 group-hover:bg-white/10" />

            <div className="relative flex h-full flex-col justify-between gap-5">
              <div className="flex items-start justify-between gap-4">
                <div className="text-start">
                  <p className="text-sm font-medium text-slate-500 transition-colors group-hover:text-slate-200">
                    {item.title}
                  </p>
                  <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 transition-colors group-hover:text-white">
                    {formatValue(item.value)}
                  </p>
                </div>

                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${item.accent} text-white shadow-lg shadow-slate-900/10 transition-transform duration-300 group-hover:scale-105`}
                >
                  <item.icon size={22} strokeWidth={2.2} />
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-start text-sm leading-6 text-slate-600 transition-colors group-hover:text-slate-200">
                  {item.description}
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold text-brand-light transition-colors group-hover:text-white">
                  <span>Open details</span>
                  <ArrowUpRight size={16} strokeWidth={2.4} />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
